/**
 * @fileoverview Block Error Boundary
 *
 * Provides graceful error handling for individual content blocks.
 * If a block fails to render, it shows a fallback UI instead of crashing the entire page.
 *
 * SSR Exception: MUST use 'use client'
 * Reason: React Error Boundaries require client-side error handling APIs
 */
"use client";

import * as React from "react";

interface BlockErrorBoundaryProps {
	children: React.ReactNode;
	blockType: string;
}

interface BlockErrorBoundaryState {
	hasError: boolean;
	error: Error | null;
}

/**
 * Error boundary that catches render errors in individual content blocks
 * and provides fallback UI instead of crashing the entire page.
 */
export class BlockErrorBoundary extends React.Component<
	BlockErrorBoundaryProps,
	BlockErrorBoundaryState
> {
	constructor(props: BlockErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false, error: null };
	}

	static getDerivedStateFromError(error: Error): BlockErrorBoundaryState {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
		if (process.env.NODE_ENV === "development") {
			console.error(
				`[ContentRenderer] Error rendering block "${this.props.blockType}":`,
				error,
				errorInfo,
			);
		}
	}

	render() {
		if (this.state.hasError) {
			// In production, fail silently (return null)
			// In development, show error details
			if (process.env.NODE_ENV === "development") {
				return (
					<div className="border border-destructive bg-destructive/10 p-4 rounded-lg my-4">
						<h3 className="text-destructive font-semibold mb-2">
							Error Rendering Block: {this.props.blockType}
						</h3>
						<pre className="text-xs overflow-auto">
							{this.state.error?.message}
						</pre>
					</div>
				);
			}
			return null;
		}

		return this.props.children;
	}
}
